import { Controller, useFieldArray, useFormContext } from 'react-hook-form';
import { formatTokenAmount } from 'utils/FormatNumber';
import {
  EntryKind,
  FormValues,
  OrderKind,
  isLimitBuyAmount,
  SnipeAmountSchema,
  LimitBuyAmountSchema,
  MarketBuyAmountSchema,
} from '../schema';

import { useQuery } from '@tanstack/react-query';
import Big from 'big.js';
import {
  MultiChainDWalletService,
  multiChainDWalletServiceKeys,
} from 'api/services/httpServices/DWalletService';
import { RowLabel, RowLabelledInput } from '../../components/RowLabelledInput';
import { PresetButton } from '../../components/PresetButton';
import {
  ArrowTrendingDownIcon,
  CheckCircleIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  ExclamationCircleIcon,
  MinusIcon,
  PlusIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline';
import { twMerge } from 'tailwind-merge';
import { IconButton } from 'modules/shared-components/button/IconButton';
import { last } from 'lodash';
import { ContractInfo } from 'api/types/httpsTypes/contracts';
import Button from 'modules/shared-components/button/button';
import { useEffect, useState } from 'react';
import { useSyncTokenPrice } from '../syncTokenPrice';
import {
  MobileExpandableCard,
  MobileExpandCardTrigger,
} from 'modules/shared-components/MobileExpandableCard';
import { isNotNil } from 'modules/utils/isNotNil';

import { GasEstimation } from 'modules/ethereum/EstimatedGasFee';
import Tooltip from 'modules/shared-components/tooltip';
import GasIcon from '../../../../assets/img/icons/gas-icon.svg';
import {
  chainAsset,
  chainBuyBribe,
  chainDefaultBuyAmountInBaseAsset,
  chainHasBribe,
  chainHasMemPool,
  chainMaxBuyGasPriority,
  chainPriorityGasFeeTitle,
  chainPriorityGasFeeTooltip,
  Chains,
  gasPrioritySuffix,
} from 'api/types/httpsTypes/d-wallets';
import { calculateWordWidth } from 'modules/shared-components/input/number-input/calculateLengthPixel';
import { useUserState } from 'modules/user/UserContext';
import { DexCard } from '../../components/cards';
import { DexPanelButton } from '../../components/tabs';
import NumberInput from '../../components/inputs/number-input';
import Checkbox from '../../components/inputs/Checkbox';
import { fromAmount } from '../../components/chains/units';

export function BuyAmount({
  disabled,
  contract,
}: {
  disabled: boolean;
  contract: ContractInfo | undefined;
}) {
  const {
    control,
    setValue,
    watch,
    formState: { errors },
  } = useFormContext<FormValues>();

  const entryKind = watch('entryKind');
  const orderKind = watch('buyOrder')?.kind;
  const [openBox, setOpenBox] = useState(false);

  useSyncTokenPrice(
    contract?.liquidityInfo.activePools[0]?.priceDollar || null
  );

  const selectedPool = contract?.liquidityInfo?.activePools[0];

  const currentPriceDollar = selectedPool?.priceDollar;
  const isDisabledWithLimitOrders = watch('buyOrder.entries.orders')?.length
    ? false
    : disabled;

  const defaultLimitOrder = [
    {
      priceDeviationPercentage: 5,
      amount: fromAmount(
        chainDefaultBuyAmountInBaseAsset(contract?.generalInfo.chain.key),
        contract?.generalInfo.chain.key,
        'weiToEther'
      ),
      price: currentPriceDollar ? currentPriceDollar * 0.95 : ('' as any),
    },
  ];

  const { kind: buyOrderKind, entries: buyOrderSettings } = watch('buyOrder');

  const { fields } = useFieldArray({
    control,
    name: 'buyOrder.entries.orders',
    keyName: 'key',
  });

  const isBuyError =
    entryKind !== EntryKind.Snipe &&
    errors?.buyOrder?.entries &&
    ('maxBuyGasPriority' in errors.buyOrder.entries ||
      'slippage' in errors.buyOrder.entries);

  useEffect(() => {
    if (orderKind === OrderKind.Limit && fields) {
      fields.map((row, index) => {
        if (!row.price && currentPriceDollar) {
          setValue(
            `buyOrder.entries.orders.${index}.price`,
            currentPriceDollar * (1 - (row.priceDeviationPercentage || 0) / 100)
          );
        }
      });
    }
  }, [currentPriceDollar, fields]);

  function snipeLaunchedTokensMobile(
    buyOrder: LimitBuyAmountSchema | MarketBuyAmountSchema
  ) {
    return (
      <div className="flex space-x-1 items-center xxs:text-dex-white-secondary">
        <div>|</div>
        <div className="flex xxs:space-x-1 items-center">
          <img className="h-4 w-4" alt="gas icon" src={GasIcon} />
          {buyOrder?.maxBuyGasPriority ? (
            <div className="xxs:text-dex-white">
              {buyOrder?.maxBuyGasPriority}{' '}
            </div>
          ) : (
            <div>--</div>
          )}
        </div>
        <div>|</div>
        <div className="flex xxs:space-x-1 items-center">
          <ArrowTrendingDownIcon className="h-4 w-4" />
          <div className="xxs:text-dex-white">
            {isNotNil(buyOrder?.slippage) ? <>{buyOrder?.slippage}%</> : '--'}
          </div>
        </div>

        <div>|</div>
        {chainHasMemPool(contract?.generalInfo.chain.key) && (
          <div>
            {buyOrder.antiMev ? (
              <div className="flex space-x-1 items-center">
                <div className="xxs:text-dex-white-secondary">MEV</div>
                <CheckCircleIcon className="w-5 h-5 xxs:text-dex-white" />
              </div>
            ) : (
              <div className="flex space-x-1 items-center">
                <div className="xxs:text-dex-white-secondary">MEV</div>
                <XCircleIcon className="w-5 h-5 xxs:text-dex-white" />
              </div>
            )}
          </div>
        )}
      </div>
    );
  }

  function snipeBlockZeroMobile(buyOrder: SnipeAmountSchema) {
    return (
      <div className="space-x-2 flex items-center text-sm xxs:text-dex-white-secondary">
        <div>|</div>
        <div className="flex xxs:gap-2 items-center">
          <div>
            Bribe:{' '}
            {isNotNil(buyOrder.bribe) ? (
              <span className="xxs:text-dex-white">
                {buyOrder.bribe || '--'}{' '}
              </span>
            ) : (
              '--'
            )}
          </div>
        </div>
        <div>|</div>
        <div className="flex xxs:gap-2 items-center ">
          {buyOrder.blockOneRetry ? (
            <div className="flex space-x-1 items-center">
              <div>Auto-retry</div>
              <CheckCircleIcon className="w-5 h-5 xxs:text-dex-white" />
            </div>
          ) : (
            <div className="flex space-x-1 items-center">
              <div>Auto-retry</div>
              <XCircleIcon className="w-5 h-5 xxs:text-dex-white" />
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <MobileExpandableCard
      mobileLabel="Buy Settings"
      renderTrigger={({ open }) => (
        <MobileExpandCardTrigger onClick={open}>
          <div className="flex justify-between items-center text-sm">
            <div className="flex space-x-1 items-center">
              {errors.buyOrder ? (
                <div className="flex space-x-1 items-center xxs:text-red-500">
                  <ExclamationCircleIcon className="xxs:w-6 h-6" />
                  <div>Missing Information</div>
                </div>
              ) : (
                <>
                  <div>Buy:</div>
                  <div className="xxs:text-dex-white">
                    {isLimitBuyAmount(buyOrderKind, buyOrderSettings) ? (
                      <>{buyOrderSettings.orders.length} order(s)</>
                    ) : (
                      <>
                        {buyOrderSettings.amount ? (
                          <>{formatTokenAmount(buyOrderSettings.amount)} </>
                        ) : (
                          '--'
                        )}
                      </>
                    )}
                  </div>
                  {watch('buyOrder.kind') === OrderKind.Snipe &&
                    snipeBlockZeroMobile(
                      watch('buyOrder.entries') as SnipeAmountSchema
                    )}
                  {watch('buyOrder.kind') !== OrderKind.Snipe &&
                    snipeLaunchedTokensMobile(
                      watch('buyOrder.entries') as
                        | MarketBuyAmountSchema
                        | LimitBuyAmountSchema
                    )}
                </>
              )}
            </div>
          </div>
        </MobileExpandCardTrigger>
      )}
    >
      <DexCard className="xxs:gap-y-2 lg:gap-y-0" size="xs" as="div">
        <div className="flex items-center justify-between ">
          <div className="hidden lg:block font-bold text-sm">Buy</div>
          <div className="grid grid-flow-col xxs:gap-1 xxs:w-full lg:w-auto">
            {entryKind !== EntryKind.Snipe && (
              <>
                <DexPanelButton
                  className="xxs:p-2 lg:py-1 lg:px-2"
                  disabled={disabled}
                  onClick={() => {
                    if (watch('safetyMeasures.autoRetryEnabled')) {
                      setValue('safetyMeasures.autoRetry.buy.active', false);
                      setValue('safetyMeasures.autoRetry.buy.nRetries', 0);
                    }
                    setValue('buyOrder', {
                      kind: OrderKind.Market,
                      entries: {
                        amount: fromAmount(
                          chainDefaultBuyAmountInBaseAsset(
                            contract?.generalInfo.chain.key
                          ),
                          contract?.generalInfo.chain.key,
                          'weiToEther'
                        ),
                        slippage: 15,
                        bribe: fromAmount(
                          chainBuyBribe(watch('chain') ?? Chains.Ethereum),
                          contract?.generalInfo.chain.key,
                          'weiToEther'
                        ),
                        maxBuyGasPriority:
                          watch('buyOrder.entries.maxBuyGasPriority') ??
                          chainMaxBuyGasPriority(
                            watch('chain') ?? Chains.Ethereum
                          ),
                        antiMev: false,
                      },
                    });
                  }}
                  isActive={orderKind === OrderKind.Market}
                >
                  <div className="xxs:text-sm lg:text-xs ">Market</div>
                </DexPanelButton>
                <DexPanelButton
                  className="xxs:p-2 lg:py-1 lg:px-2"
                  onClick={() => {
                    setValue('buyOrder', {
                      kind: OrderKind.Limit,
                      entries: {
                        slippage: 15,
                        antiMev: false,
                        maxBuyGasPriority:
                          watch('buyOrder.entries.maxBuyGasPriority') ??
                          chainMaxBuyGasPriority(
                            watch('chain') ?? Chains.Ethereum
                          ),
                        bribe: fromAmount(
                          chainBuyBribe(watch('chain') ?? Chains.Ethereum),
                          contract?.generalInfo.chain.key,
                          'weiToEther'
                        ),
                        expirationInHours: 24,
                        orders: defaultLimitOrder,
                      },
                    });

                    setValue('safetyMeasures.autoRetry.buy.active', true);
                    setValue('safetyMeasures.autoRetry.buy.nRetries', 1);
                    setValue('safetyMeasures.autoRetryEnabled', true);
                  }}
                  disabled={disabled}
                  isActive={orderKind === OrderKind.Limit}
                >
                  <div className="xxs:text-sm lg:text-xs">Limit</div>
                </DexPanelButton>
              </>
            )}
          </div>
        </div>

        {(orderKind === OrderKind.Market ||
          orderKind === OrderKind.Snipe ||
          orderKind === OrderKind.SnipeWithoutMemPool) && (
          <MarketBuy disabled={disabled} />
        )}
        {orderKind === OrderKind.Limit && (
          <LimitBuy currentPriceDollar={currentPriceDollar} />
        )}

        {entryKind === EntryKind.Snipe &&
          chainHasMemPool(contract?.generalInfo.chain.key) && (
            <>
              <RowLabelledInput
                label={
                  <RowLabel
                    inputSize="xs"
                    label="Bribing Fee"
                    tooltip="The amount of gas fee as a tip you will pay to the miner to get ahead of other snipers and be the first to buy. In case the block-0 transaction fails, you won't lose your tip."
                  />
                }
                className="xxs:py-1"
              >
                <Controller
                  name="buyOrder.entries.bribe"
                  control={control}
                  defaultValue={'' as any}
                  render={({ field, fieldState: { error } }) => (
                    <NumberInput
                      {...field}
                      id="buyOrder.entries.bribe"
                      hideErrorMessage={true}
                      disabled={disabled}
                      inputSize="xs"
                      name="buyOrder.entries.amount"
                      className="text-right"
                      placeholder="0.00"
                      extraStyle={{
                        paddingRight: `${
                          calculateWordWidth(
                            chainAsset(contract?.generalInfo.chain.key) ?? 0,
                            12
                          ) + 20
                        }px`,
                      }}
                      suffix={
                        <>{chainAsset(contract?.generalInfo.chain.key)}</>
                      }
                      value={field.value}
                      error={error?.message}
                    />
                  )}
                />
              </RowLabelledInput>

              <Controller
                name={'buyOrder.entries.blockOneRetry'}
                defaultValue={'' as any}
                control={control}
                render={({ field }) => {
                  return (
                    <div className="flex items-center xxs:space-x-1 ">
                      <Checkbox
                        checked={field.value}
                        onChange={field.onChange}
                        disabled={disabled}
                        className="xxs:w-5 xxs:h-5 lg:h-4 lg:w-4"
                        id="buyOrder.entries.blockOneRetry"
                      />
                      <Tooltip text="When enabled, the bot will automatically retry to snipe at block-1, in case the block-0 snipe fails.">
                        <div className="flex space-x-1 items-center">
                          <div className="xxs:text-sm lg:text-xs xxs:text-dex-white-secondary hover:text-dex-white">
                            Auto Retry
                          </div>
                        </div>
                      </Tooltip>
                    </div>
                  );
                }}
              />

              {watch('buyOrder.entries.blockOneRetry') && (
                <div className="flex xxs:gap-2 justify-between">
                  <Controller
                    name="buyOrder.entries.backupBribe"
                    control={control}
                    defaultValue={'' as any}
                    render={({ field, fieldState: { error } }) => (
                      <NumberInput
                        inputSize="xs"
                        suffix={
                          <>{chainAsset(contract?.generalInfo.chain.key)}</>
                        }
                        value={field.value as number}
                        disabled={disabled}
                        label="Backup Bribing Fee"
                        tooltip="The bribing fee that will be used when re-trying to snipe at block-1."
                        onChange={(event) =>
                          field.onChange(event.currentTarget.value)
                        }
                        error={error?.message}
                        name={''}
                      />
                    )}
                  />

                  <Controller
                    name={'buyOrder.entries.slippage'}
                    defaultValue={'' as any}
                    control={control}
                    render={({ field, fieldState: { error } }) => {
                      return (
                        <NumberInput
                          suffix={<>%</>}
                          {...field}
                          inputSize="xs"
                          hideErrorMessage={true}
                          disabled={disabled}
                          label="Slippage"
                          tooltip="The slippage that will applied when retry the snipe at block-1."
                          error={error?.message}
                          name={''}
                        />
                      );
                    }}
                  />
                </div>
              )}
              <GasEstimation
                chain={watch('chain')}
                bribe={watch('buyOrder.entries.bribe')}
              />
            </>
          )}

        {(entryKind !== EntryKind.Snipe ||
          !chainHasMemPool(contract?.generalInfo.chain.key)) && (
          <>
            <button
              type="button"
              onClick={() => setOpenBox(!openBox)}
              className=" m-0 p-0 font-normal normal-case justify-start w-full"
            >
              <div
                className={twMerge(
                  'flex justify-between items-center  xxs:text-dex-white-secondary hover:text-dex-white',
                  isBuyError && 'xxs:text-red-500'
                )}
              >
                <Tooltip text="Set-up buy gas priority, slippage, and MEV protection.">
                  <div className="flex space-x-1 items-center">
                    {isBuyError && (
                      <ExclamationCircleIcon className="xxs:text-red-500 xxs:w-4 h-4" />
                    )}
                    <div className="xxs:text-sm lg:text-xs ">
                      Gas/Slippage
                      {chainHasMemPool(contract?.generalInfo.chain.key)
                        ? ', MEV protection'
                        : ''}
                    </div>
                  </div>
                </Tooltip>
                <div className="">
                  {openBox ? (
                    <ChevronUpIcon className="h-4 w-4" />
                  ) : (
                    <ChevronDownIcon className="h-4 w-4" />
                  )}
                </div>
              </div>
            </button>
            {openBox && (
              <div className="space-y-0">
                <RowLabelledInput
                  label={
                    <RowLabel
                      inputSize="xs"
                      label={chainPriorityGasFeeTitle(watch('chain'))}
                      tooltip={chainPriorityGasFeeTooltip(watch('chain'))}
                    />
                  }
                  className="xxs:py-1"
                >
                  <Controller
                    name="buyOrder.entries.maxBuyGasPriority"
                    control={control}
                    defaultValue={'' as any}
                    render={({ field, fieldState: { error } }) => (
                      <NumberInput
                        containerClassName="xxs:w-[108px]"
                        inputSize="xs"
                        disabled={isDisabledWithLimitOrders}
                        suffix={
                          <>
                            {gasPrioritySuffix(contract?.generalInfo.chain.key)}
                          </>
                        }
                        value={field.value}
                        onChange={(event) =>
                          field.onChange(event.currentTarget.value)
                        }
                        error={error?.message}
                        name={''}
                      />
                    )}
                  />
                </RowLabelledInput>

                {chainHasMemPool(contract?.generalInfo.chain.key) &&
                watch('buyOrder.entries.slippage') &&
                (watch('buyOrder.entries.slippage') as number) > 25 &&
                !watch('buyOrder.entries.antiMev') ? (
                  <div className="flex space-x-1 items-center">
                    <ExclamationCircleIcon className="xxs:text-red-500 w-4 h-4" />
                    <p className="text-red-500 mb-0">
                      High slippage, enable MEV protection to avoid bots.
                    </p>
                  </div>
                ) : (
                  ''
                )}
                <RowLabelledInput
                  label={
                    <RowLabel
                      inputSize="xs"
                      label="Slippage"
                      tooltip="The slippage threshold for executing your buy transaction. In case of limit orders, the same slippage is applied to each limit order step."
                    />
                  }
                  className="xxs:py-1"
                >
                  <Controller
                    name={'buyOrder.entries.slippage'}
                    defaultValue={'' as any}
                    control={control}
                    render={({ field, fieldState: { error } }) => {
                      return (
                        <NumberInput
                          inputSize="xs"
                          containerClassName="xxs:w-[108px]"
                          suffix={<>%</>}
                          disabled={isDisabledWithLimitOrders}
                          {...field}
                          error={error?.message}
                          name={''}
                        />
                      );
                    }}
                  />
                </RowLabelledInput>
                {chainHasMemPool(contract?.generalInfo.chain.key) && (
                  <RowLabelledInput
                    label={
                      <RowLabel
                        inputSize="xs"
                        label="MEV Protection"
                        tooltip="When active, the buy transaction will be executed as a private transaction. This will avoid your transaction to be front-run or sandwiched."
                      />
                    }
                    className="xxs:py-1"
                  >
                    <Controller
                      name={'buyOrder.entries.antiMev'}
                      defaultValue={'' as any}
                      control={control}
                      render={({ field }) => {
                        return (
                          <div className="flex items-center xxs:w-[108px] justify-end">
                            <Checkbox
                              disabled={isDisabledWithLimitOrders}
                              className="xxs:w-4 xxs:h-4"
                              checked={field.value}
                              onClick={field.onChange}
                              onChange={() => {
                                if (
                                  !field.value &&
                                  chainHasBribe(contract?.generalInfo.chain.key)
                                ) {
                                  setValue(
                                    'buyOrder.entries.bribe',
                                    fromAmount(
                                      chainBuyBribe(
                                        contract?.generalInfo.chain.key
                                      ),
                                      contract?.generalInfo.chain.key,
                                      'weiToEther'
                                    )
                                  );
                                } else {
                                  setValue('buyOrder.entries.bribe', 0);
                                }
                              }}
                              id="buyOrder.entries.antiMev"
                            />
                            <Checkbox.Label htmlFor="anti-mev">
                              <div className="xxs:text-sm lg:text-xs">
                                Enabled
                              </div>
                            </Checkbox.Label>
                          </div>
                        );
                      }}
                    />
                  </RowLabelledInput>
                )}
                {chainHasBribe(contract?.generalInfo.chain.key) &&
                  watch('buyOrder.entries.antiMev') && (
                    <RowLabelledInput
                      label={
                        <RowLabel
                          inputSize="xs"
                          label="Bribe Amount"
                          tooltip="Bribe is used to make transaction faster when using anti-mev."
                        />
                      }
                      className="xxs:py-1"
                    >
                      <Controller
                        name="buyOrder.entries.bribe"
                        control={control}
                        defaultValue={'' as any}
                        render={({ field, fieldState: { error } }) => (
                          <NumberInput
                            containerClassName="xxs:w-[108px]"
                            inputSize="xs"
                            disabled={isDisabledWithLimitOrders}
                            suffix={
                              <>
                                {gasPrioritySuffix(
                                  contract?.generalInfo.chain.key
                                )}
                              </>
                            }
                            value={field.value}
                            onChange={(event) =>
                              field.onChange(event.currentTarget.value)
                            }
                            error={error?.message}
                            name={''}
                          />
                        )}
                      />
                    </RowLabelledInput>
                  )}
              </div>
            )}
            <GasEstimation
              chain={watch('chain')}
              priorityFee={watch('buyOrder.entries.maxBuyGasPriority', 0)}
              bribe={watch('buyOrder.entries.bribe') ?? 0}
            />
          </>
        )}
      </DexCard>
    </MobileExpandableCard>
  );
}

const quickOptions = (selectedWallet: boolean) => {
  if (selectedWallet) {
    return [5, 10, 50, 100];
  } else {
    return [0.01, 0.05, 0.1, 1];
  }
};

function MarketBuy({ disabled }: { disabled: boolean }) {
  const { control, setValue, watch } = useFormContext<FormValues>();

  const selectedWallets = watch('wallets') || [];
  const chain = watch('chain');
  const { user } = useUserState();
  const { data } = useQuery({
    queryFn: () => MultiChainDWalletService.getWallets(chain),
    enabled: !!chain && !!user,
    queryKey: multiChainDWalletServiceKeys.getWallets(chain),
  });

  const selectedWallet =
    selectedWallets && selectedWallets.length === 1
      ? data?.find((w) => w.id === selectedWallets[0])
      : null;

  return (
    <div className="xxs:space-y-1">
      <Controller
        name="buyOrder.entries.amount"
        defaultValue={'' as any}
        control={control}
        render={({ field, fieldState: { error } }) => {
          return (
            <NumberInput
              {...field}
              hideErrorMessage={true}
              inputSize="xs"
              name="buyOrder.entries.amount"
              prefix={`Amount`}
              className="text-right"
              placeholder="0.00"
              disabled={disabled}
              extraStyle={{
                paddingRight: `${
                  calculateWordWidth(chainAsset(chain) ?? 0, 12) + 20
                }px`,
              }}
              suffix={<>{chainAsset(chain)}</>}
              error={error?.message}
            />
          );
        }}
      />
      <div className="flex xxs:gap-1 mb-1">
        {quickOptions(!!selectedWallet).map((amount) => (
          <PresetButton
            key={amount}
            type="button"
            disabled={disabled}
            onClick={() => {
              if (selectedWallet && selectedWallet?.chainBalance) {
                setValue(
                  'buyOrder.entries.amount',
                  Big(selectedWallet.chainBalance)
                    .times(amount)
                    .div(100)
                    .round(4, Big.roundDown)
                    .toNumber(),
                  {
                    shouldDirty: true,
                  }
                );
              } else {
                setValue('buyOrder.entries.amount', amount);
              }
            }}
          >
            <div className="xxs:text-sm lg:text-xs ">
              {amount}
              {selectedWallet && '%'}
            </div>
          </PresetButton>
        ))}
      </div>
    </div>
  );
}

function LimitBuy({
  currentPriceDollar,
}: {
  currentPriceDollar?: number | null;
}) {
  const { control, setValue, watch } = useFormContext<FormValues>();
  const isEditing = window.location.href.includes('edit');
  const [showDeviation, setShowDeviation] = useState(!isEditing);

  const chain = watch('chain');

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'buyOrder.entries.orders',
    keyName: 'key',
  });

  return (
    <div className="xxs:space-y-2 lg:space-y-1">
      <div>
        <div className="grid grid-cols-[minmax(0,1fr)_minmax(0,1fr)_36px] xxs:gap-1 xxs:text-xs dark:text-black-50 xxs:pb-0.5">
          <div className="flex gap-1">
            {showDeviation ? (
              <div className="xxs:text-dex-white-secondary">Deviation</div>
            ) : (
              <div className="xxs:text-dex-white-secondary">Price</div>
            )}{' '}
            {!isEditing && (
              <Button
                variant="dexNeutral"
                className="m-0 py-0 px-1"
                type="button"
                onClick={() => setShowDeviation(!showDeviation)}
              >
                {showDeviation ? (
                  <div className="text-xs">$</div>
                ) : (
                  <div className="text-xs">%</div>
                )}
              </Button>
            )}
          </div>
          <div className="xxs:text-dex-white-secondary">Amount</div>
        </div>
        <div className="flex flex-col xxs:gap-1">
          {fields.map((row, index) => (
            <div
              className={twMerge(
                'grid grid-cols-[minmax(0,1fr)_minmax(0,1fr)_30px] xxs:gap-1'
              )}
              key={row.key}
            >
              {showDeviation ? (
                <Controller
                  key="deviation"
                  name={`buyOrder.entries.orders.${index}.priceDeviationPercentage`}
                  defaultValue={'' as any}
                  control={control}
                  render={({ field, fieldState: { error } }) => {
                    return (
                      <NumberInput
                        {...field}
                        onChange={(event) => {
                          field.onChange(event.currentTarget.value);
                          setValue(
                            `buyOrder.entries.orders.${index}.price`,
                            currentPriceDollar
                              ? (Big(currentPriceDollar)
                                  .times(
                                    Big(1).minus(
                                      Big(event.currentTarget.value).div(100)
                                    )
                                  )

                                  .toFixed(15) as any)
                              : 0
                          );
                        }}
                        inputSize="xs"
                        suffix={<>%</>}
                        prefix={'-'}
                        className={twMerge(
                          row.isFailed &&
                            'xxs:!bg-red-600/40 xxs:!text-white-100'
                        )}
                        hideErrorMessage={true}
                        disabled={row.status && row.status !== 'ACTIVE'}
                        error={error?.message}
                      />
                    );
                  }}
                />
              ) : (
                <Controller
                  key="price"
                  name={`buyOrder.entries.orders.${index}.price`}
                  defaultValue={'' as any}
                  control={control}
                  render={({ field, fieldState: { error } }) => {
                    return (
                      <NumberInput
                        {...field}
                        inputSize="xs"
                        name={`buyOrder.entries.orders.${index}.price`}
                        onChange={(event) => {
                          field.onChange(event.currentTarget.value);

                          setValue(
                            `buyOrder.entries.orders.${index}.priceDeviationPercentage`,
                            currentPriceDollar
                              ? Big(1)
                                  .minus(
                                    +Big(event.currentTarget.value).div(
                                      currentPriceDollar
                                    )
                                  )
                                  .times(100)
                                  .round(4)
                                  .toNumber()
                              : 0
                          );
                        }}
                        prefix={'$'}
                        hideErrorMessage={true}
                        className={twMerge(
                          'text-right',
                          row.isFailed &&
                            'xxs:!bg-red-600/40 xxs:!text-white-100'
                        )}
                        disabled={row.status && row.status !== 'ACTIVE'}
                        error={error?.message}
                      />
                    );
                  }}
                />
              )}
              <Controller
                name={`buyOrder.entries.orders.${index}.amount`}
                defaultValue={'' as any}
                control={control}
                render={({ field, fieldState: { error } }) => {
                  return (
                    <NumberInput
                      {...field}
                      inputSize="xs"
                      name={`buyOrder.entries.orders.${index}.amount`}
                      placeholder="0.00"
                      extraStyle={{
                        paddingRight: `${
                          calculateWordWidth(chainAsset(chain) ?? 0, 12) + 20
                        }px`,
                      }}
                      suffix={<>{chainAsset(chain)}</>}
                      className={twMerge(
                        'text-right',
                        row.isFailed && 'xxs:!bg-red-600/40 xxs:!text-white-100'
                      )}
                      disabled={row.status && row.status !== 'ACTIVE'}
                      error={error?.message}
                      hideErrorMessage={true}
                    />
                  );
                }}
              />

              {index === 0 ? (
                <IconButton
                  className="xxs:bg-dex-black-600 hover:bg-dex-black-500 xxs:text-dex-white-secondary hover:text-dex-white xxs:p-1 xxs:rounded"
                  type="button"
                  onClick={() =>
                    append({
                      priceDeviationPercentage:
                        +(last(fields)?.priceDeviationPercentage || 0) + 5,
                      amount: 0.05,
                      price: currentPriceDollar
                        ? currentPriceDollar *
                          (1 -
                            ((last(fields)?.priceDeviationPercentage || 0) +
                              5) /
                              100)
                        : ('' as any),
                    })
                  }
                >
                  <PlusIcon className="h-3 w-3" aria-hidden="true" />
                </IconButton>
              ) : (
                <IconButton
                  className="xxs:bg-dex-black-600 hover:bg-dex-black-500 xxs:text-dex-white-secondary hover:text-dex-white xxs:p-1 xxs:rounded"
                  type="button"
                  disabled={
                    (row.status && row.status !== 'ACTIVE') || row.isFailed
                  }
                  onClick={() => remove(index)}
                >
                  <MinusIcon className="h-3 w-3" aria-hidden="true" />
                </IconButton>
              )}
            </div>
          ))}
        </div>
      </div>
      <RowLabelledInput
        label={
          <RowLabel
            label="Expiration"
            inputSize="xs"
            tooltip="The expiration time (in hours) after which the limit order will be considered expired."
          />
        }
        className="xxs:py-1"
      >
        <Controller
          name={'buyOrder.entries.expirationInHours'}
          defaultValue={'' as any}
          control={control}
          render={({ field, fieldState: { error } }) => {
            return (
              <NumberInput
                {...field}
                inputSize="xs"
                containerClassName="xxs:w-[108px]"
                suffix={<>hr</>}
                error={error?.message}
              />
            );
          }}
        />
      </RowLabelledInput>
    </div>
  );
}
